import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './Constants';

const ViewSessions = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null); // Track which session is being deleted
  
  // In a real application, you would get the student ID from authentication context
//   const studentId = 1; // Replace with dynamic student ID from your auth system

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/sessions`);
      const filteredData = res.data;
      
      // Sort by ID in descending order (newest first)
      const sortedData = filteredData.sort((a, b) => b.id - a.id);
      
      setSessions(sortedData);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching sessions", err);
      setError("Failed to load sessions. Please try again later.");
      setLoading(false);
    }
  };

  // Delete session function
  const deleteSession = async (sessionId) => {
    // Confirm before deleting
    if (!window.confirm('Are you sure you want to delete this session? This action cannot be undone.')) {
      return;
    }

    setDeletingId(sessionId);
    
    try {
      await axios.delete(`${API_BASE_URL}/api/sessions/${sessionId}`);
      
      // Remove the deleted session from state
      setSessions(prevSessions => prevSessions.filter(session => session.id !== sessionId));
      
      // Show success message
      alert('Session deleted successfully!');
    } catch (err) {
      console.error("Error deleting session", err);
      alert('Failed to delete session. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  // Format date and time for display
  const formatDateTime = (dateTimeString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit', 
      minute: '2-digit'
    };
    return new Date(dateTimeString).toLocaleDateString('en-US', options);
  };

  // Get status badge color based on session status
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'scheduled':
        return 'badge badge-info';
      case 'completed':
        return 'badge badge-success';
      case 'cancelled':
        return 'badge badge-error';
      case 'no_show':
        return 'badge badge-warning';
      default:
        return 'badge badge-neutral';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <span className="loading loading-spinner loading-lg text-primary"></span>
          <p className="mt-4 text-lg">Loading sessions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error max-w-4xl mx-auto my-8">
        <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-center">Scheduled Sessions</h1>
        <p className="text-center text-gray-500 mt-2">
          View all your upcoming and past tutoring sessions
        </p>
      </div>

      {sessions.length === 0 ? (
        <div className="card bg-base-100 shadow-xl max-w-2xl mx-auto">
          <div className="card-body text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h2 className="card-title justify-center mt-4">No sessions found</h2>
            <p>You don't have any scheduled sessions yet.</p>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th>Session ID</th>
                <th>Course</th>
                <th>Tutor</th>
                <th>Student</th>
                <th>Scheduled Time</th>
                <th>Duration</th>
                <th>Status</th>
                <th>Meeting Link</th>
                <th>Created on</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((session) => (
                <tr key={session.id}>

                    <td>{session.id}</td>
                  <td>
                    <div className="font-bold">{session.enrollment?.course_name}</div>
                    <div className="text-sm opacity-50">
                      {session.enrollment?.subject} - {session.enrollment?.grade} Grade
                    </div>
                    <div className="text-sm opacity-50">
                      {session.enrollment?.syllabus}
                    </div>
                  </td>
                  <td>
                    <div className="font-bold">
                      {session.enrollment?.tutor?.name || 'Tutor Name'}
                    </div>
                    <div className="text-sm opacity-50">
                      {session.enrollment?.type}
                    </div>
                  </td>
                  <td>
                    <div className="font-bold">
                      {session.enrollment?.student?.name || 'Student Name'}
                    </div>
                  </td>
                  <td>
                    {formatDateTime(session.scheduled_time)}
                  </td>
                  <td>
                    {session.duration_minutes} minutes
                  </td>
                  <td>
                    <div className={getStatusBadgeClass(session.status)}>
                      {session.status}
                    </div>
                  </td>
                  <td>
                    {session.meeting_link ? (
                      <a 
                        href={session.meeting_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline btn-primary"
                      >
                        Join Meeting
                      </a>
                    ) : (
                      <span className="text-gray-400">Not available</span>
                    )}
                  </td>
                  <td>
                    {formatDateTime(session.created_at)}
                  </td>
                  <td>
                    <button
                      onClick={() => deleteSession(session.id)}
                      disabled={deletingId === session.id}
                      className="btn btn-sm btn-error"
                    >
                      {deletingId === session.id ? (
                        <>
                          <span className="loading loading-spinner loading-xs"></span>
                          Deleting...
                        </>
                      ) : (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          
                        </>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ViewSessions;